// This component creates a horizontal tab set with responsive UX.
// On desktop, the tabs are displayed inline.
// On mobile, the tabs are displayed in a horizontal scrollable container with fade edges.
// On desktop with narrow width, the tabs are displayed in a horizontal scrollable container with fade edges as well as with caret buttons to scroll tabs left and right.

import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'astroturf/react';

const SCROLL_STEP = 150;

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

// Desktop: float left so tabs sit inline with the datepicker/export div.
// Narrow: flex row with horizontal scroll, fades, and caret buttons.
const Wrapper = styled.div`
  float: left;

  @media (max-width: 999px) {
    float: none;
    display: flex;
    align-items: stretch;
    min-width: 0;
  }
`;

const Track = styled.div`
  @media (max-width: 999px) {
    flex: 1 1 0%;
    min-width: 0;
    overflow: hidden;
  }
`;

const FadeTrack = styled(Track)`
  $fade-width: 24px;

  &.fadeRight:not(.fadeLeft) {
    -webkit-mask-image: linear-gradient(
      to left,
      transparent,
      black $fade-width
    );
    mask-image: linear-gradient(to left, transparent, black $fade-width);
  }

  &.fadeLeft:not(.fadeRight) {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent,
      black $fade-width
    );
    mask-image: linear-gradient(to right, transparent, black $fade-width);
  }

  &.fadeLeft.fadeRight {
    -webkit-mask-image: linear-gradient(
      to right,
      transparent,
      black $fade-width,
      black calc(100% - $fade-width),
      transparent
    );
    mask-image: linear-gradient(
      to right,
      transparent,
      black $fade-width,
      black calc(100% - $fade-width),
      transparent
    );
  }
`;

const NavList = styled.ul`
  display: flex !important;
  flex-wrap: nowrap !important;
  list-style: none;
  padding-left: 0;
  margin: 0;
  white-space: nowrap;

  @media (max-width: 999px) {
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  > li,
  > li.u-floatLeft {
    flex: 0 0 auto !important;
    float: none !important;
  }
`;

const Caret = styled.span`
  display: none;

  @media (max-width: 999px) {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    background: none;
    border: none;
    padding: 0 6px;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    user-select: none;

    &.hidden {
      visibility: hidden;
      pointer-events: none;
    }
  }
`;

export const ScrollableTabNav = ({ children }) => {
  const [isTouch, setIsTouch] = useState(isTouchDevice);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef(null);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsTouch(isTouchDevice());
      updateScrollState();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateScrollState]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * SCROLL_STEP, behavior: 'smooth' });
  };

  return (
    <Wrapper>
      {!isTouch && (
        <Caret
          role="button"
          hidden={!canScrollLeft}
          onClick={() => scroll(-1)}
          aria-label="Scroll tabs left"
          tabIndex={canScrollLeft ? 0 : -1}
        >
          ‹
        </Caret>
      )}
      <FadeTrack fadeLeft={canScrollLeft} fadeRight={canScrollRight}>
        <NavList ref={scrollRef} className="nav navbar-nav">
          {children}
        </NavList>
      </FadeTrack>
      {!isTouch && (
        <Caret
          role="button"
          hidden={!canScrollRight}
          onClick={() => scroll(1)}
          aria-label="Scroll tabs right"
          tabIndex={canScrollRight ? 0 : -1}
        >
          ›
        </Caret>
      )}
    </Wrapper>
  );
};
